import React, { useState } from "react";
import { Field, Form, useForm, useFormState, useField } from "react-final-form";
import ValidatedInput from "../../common/ValidatedInput";
import { getCountries, validateVat } from "../../../queries/organization";
import PropTypes from "prop-types";
import isEmpty from "lodash/isEmpty";
import { OnChange } from "react-final-form-listeners";
import CustomSelect from "../../common/CustomSelect";
import { useQuery } from "react-query";
import { Spinner } from "reactstrap";
import ButtonSpinner from "../../common/ButtonSpinner";

const OrganizationForm = ({ onSubmit, organization }) => {
  const [vatValidationError, setVatValidationError] = useState("You have to validate VAT first");
  const [vatResponse, setVatResponse] = useState(null);
  const [vatNumber, setVatNumber] = useState(null);
  const [vatValDatetime, setVatValDatetime] = useState(null);
  const [btnVatValDisabled, setBtnVatValDisabled] = useState(false)
  const [firstSubmit, setFirstSubmit] = useState(false);


  const {
    isLoading,
    isError,
    data: countries,
  } = useQuery(
    "countries",
    () =>
      getCountries().then((data) => {
        return data;
      }),
    {
      keepPreviousData: true,
    }
  );

  const disableButton = () => {
    setBtnVatValDisabled(true);
    // console.log("Wait for 5 seconds...");
    setTimeout(() => {
      setBtnVatValDisabled(false);
    }, 3000);
  };

  const handleVatValidation = async (value) => {
    setVatValDatetime(Date());
    if (!isEmpty(value)) {
      await validateVat({ vat: value })
        .then((data) => {
          setVatValidationError("");
          setVatResponse(data);
        })
        .catch((error) => {
          setVatResponse(null);
          setVatValidationError(
            `Validation failed ${error.response.data.errors[0].detail}`
          );
          return error;
        });
    } else {
      setVatResponse(null);
      setVatValidationError("");
    }
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={organization}
      validate={(values) => {
        const errors = {};
        if (!values.legalName) {
          errors.legalName = "Legal Name is Required";
        }
        if (!values.vat) {
          errors.vat = "VAT number is Required";
        }
        if (vatValidationError != "") {
          errors.vat = "First validate your VAT using the button"
        }
        if (!values.billingAddress) {
          errors.billingAddress = "Address is Required";
        }
        if (values.email) {
          if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
            errors.email = "Invalid email address";
          }
        }
        return errors;
      }}
      render={({
        submitError,
        handleSubmit,
        form,
        submitting,
        pristine,
        values,
      }) => (
        <form onSubmit={handleSubmit}>
          {submitError && (
            <p className="text-center text-error">{submitError} </p>
          )}
          <h5 className="mt-5 mb-4">Organization Information</h5>
          <div className="fin-col">
            <label>Organization name</label>
            <Field
              inputClassName="fin-control"
              component={ValidatedInput}
              name="legalName"
              type="text"
              placeholder="eg Finclude"
            />
          </div>
          <div className="fin-col">
            {vatValidationError && (
              <strong style={{ color: "red" }}>{vatValidationError}</strong>
            )}
            <label>VAT No.</label>
            <Field
              inputClassName="fin-control"
              type="text"
              name="vat"
              id="vat-validation-box"
              parse={(value) => value.trim()}
              component={ValidatedInput}
              placeholder="eg EL101256987"
            />
            <p className="text-description">
              Structured differently per country as described by the{" "}
              <a
                href="https://ec.europa.eu/taxation_customs/vies/faqvies.do#item_11"
                target="_blank"
                className="link-color"
                rel="noreferrer"
              >
                European Commission
              </a>
            </p>
            <OnChange name='vat'>
              {(value, previous) => {
                if (!isEmpty(vatResponse)) {
                  setVatValidationError('You have to validate VAT first');
                  setVatResponse(null);
                }
              }}
            </OnChange>

            <button
              type='reset'
              disabled={btnVatValDisabled}
              className="btn btn-sm btn-main"
              onClick={
                (event) => {
                  disableButton();
                  handleVatValidation(document.getElementsByName('vat')[0].value);
                }
              }
            >
              {btnVatValDisabled ? <ButtonSpinner /> : 'Validate VAT'}
            </button>
            {!isEmpty(vatResponse) && (
              <p style={{ color: "green" }} className="text-description">
                Verified data for<strong> {vatResponse?.vat}</strong>
                <span className="d-inline-block">
                  Legal Name: <strong>{vatResponse?.legalName}</strong>,
                </span>{" "}
                <span className="d-inline-block">
                  Address: <strong>{vatResponse?.billingAddress}</strong>,
                </span>{" "}
                <span className="d-inline-block">
                  Country: <strong>{vatResponse?.country}</strong>
                </span>
              </p>
            )}
          </div>
          <h5 className="mt-5 mb-4">Billing Information</h5>
          <div className="fin-col">
            <label>Address</label>
            <Field
              inputClassName="fin-control"
              name="billingAddress"
              type="text"
              component={ValidatedInput}
              placeholder="Address"
            />
          </div>
          <div className="row row-xs">
            <div className="col-7">
              <div className="fin-col">
                <label>City</label>
                <Field
                  inputClassName="fin-control"
                  name="city"
                  component={ValidatedInput}
                  type="text"
                  placeholder="City"
                />
              </div>
            </div>
            <div className="col-5">
              <div className="fin-col">
                <label>ZIP Code</label>
                <Field
                  inputClassName="fin-control"
                  name="postalCode"
                  component={ValidatedInput}
                  type="text"
                  placeholder="Zip code"
                />
              </div>
            </div>
          </div>
          <div className="fin-col">
            <label>Country</label>
            {!isLoading && !isError && (
              <Field
                name="country"
                options={countries}
                component={CustomSelect}
                placeholder="Country"
                isSearchable={true}
                isMulti={false}
                isClearable={true}
              />
            )}
          </div>
          <div className="mt-45">
            <button
              className="btn btn-sm btn-main"
              disabled={submitting || pristine}
            >
              {isEmpty(organization)
                ? `Create organization`
                : "Update organization"}
            </button>
          </div>
        </form>
      )}
    />
  );
};

OrganizationForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  organization: PropTypes.object,
};

export default OrganizationForm;